import React, { useState } from "react";
import zones from "../../data/emergencyMockZones";

// Illustrations of the emergency preparedness map, built from the app's own
// styling (colours, totals card, label pills, mode switch, timeline). Every zone
// figure is made up; the zones are central Bergen's grunnkretser, so the map looks
// like a real set-up. Not a screenshot, not customer data.

const C = {
  up: "#16A34A",
  steady: "#F59E0B",
  down: "#DC2626",
  intl: "#2563EB",
  flat: "#9CA3AF",
  brand: "#4CAF50",
};

// --- Web Mercator, for placing OSM tiles and zones on one canvas ----------------
const TILE = 256;
const Z = 15;
const W = 960;
const H = 540;
const project = (lon, lat) => {
  const s = TILE * 2 ** Z;
  const sin = Math.sin((lat * Math.PI) / 180);
  return [((lon + 180) / 360) * s, (0.5 - Math.log((1 + sin) / (1 - sin)) / (4 * Math.PI)) * s];
};
const CENTER = project(5.3245, 60.3928);
const OX = CENTER[0] - W / 2;
const OY = CENTER[1] - H / 2;
const xy = (lon, lat) => {
  const [x, y] = project(lon, lat);
  return [x - OX, y - OY];
};
const tiles = [];
for (let tx = Math.floor(OX / TILE); tx <= Math.floor((OX + W) / TILE); tx++)
  for (let ty = Math.floor(OY / TILE); ty <= Math.floor((OY + H) / TILE); ty++) tiles.push({ tx, ty });

// Area-weighted centre of a ring, in map pixels: where a zone's label sits.
const centre = (ring) => {
  const pts = ring.map(([lon, lat]) => xy(lon, lat));
  let a = 0, cx = 0, cy = 0;
  for (let i = 0; i < pts.length - 1; i++) {
    const f = pts[i][0] * pts[i + 1][1] - pts[i + 1][0] * pts[i][1];
    a += f;
    cx += (pts[i][0] + pts[i + 1][0]) * f;
    cy += (pts[i][1] + pts[i + 1][1]) * f;
  }
  return a ? [cx / (3 * a), cy / (3 * a)] : pts[0];
};
const byName = Object.fromEntries(zones.map((z) => [z.name, z]));
const pick = (names, k) => names.reduce((s, n) => s + byName[n][k], 0);
const LABELLED = 8; // labels on the zones that changed most, so the map stays readable

const traffic = (delta, base) => {
  const band = Math.max(Math.abs(base) * 0.03, 3);
  return Math.abs(delta) <= band ? "steady" : delta > 0 ? "up" : "down";
};
const fmt = (n) => n.toLocaleString("en-GB");
const signed = (n) => `${n > 0 ? "▲ +" : n < 0 ? "▼ " : "— "}${fmt(n)}`;

const sum = (k) => zones.reduce((s, z) => s + z[k], 0);
const WATERFRONT = ["Torget", "Strandkaien", "Vetrlidsallmenningen"];
const CORE = ["Torgalmenningen", "Marken", "Domkirken"];
const totals = [
  { label: "Total city centre", present: sum("now"), intl: sum("intl") },
  { label: "Waterfront", present: pick(WATERFRONT, "now"), intl: pick(WATERFRONT, "intl"), group: true },
  { label: "City core", present: pick(CORE, "now"), intl: pick(CORE, "intl"), group: true },
];

export const MapMock = () => {
  const [mode, setMode] = useState("present");
  return (
    <figure className="epm-frame" aria-label="Illustration of the live map with made-up zones and figures">
      <div className="epm-map">
        <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid slice" role="img" aria-hidden="true">
          <defs>
            <filter id="epm-light">
              <feColorMatrix type="saturate" values="0.06" />
              <feComponentTransfer>
                <feFuncR type="linear" slope="0.55" intercept="0.44" />
                <feFuncG type="linear" slope="0.55" intercept="0.44" />
                <feFuncB type="linear" slope="0.55" intercept="0.45" />
              </feComponentTransfer>
            </filter>
          </defs>
          <g filter="url(#epm-light)">
            {tiles.map(({ tx, ty }) => (
              <image
                key={`${tx}-${ty}`}
                href={`https://tile.openstreetmap.org/${Z}/${tx}/${ty}.png`}
                x={tx * TILE - OX}
                y={ty * TILE - OY}
                width={TILE}
                height={TILE}
              />
            ))}
          </g>
          {zones.map((z) => {
            const t = mode === "present" ? traffic(z.now - z.before, z.before) : traffic(z.intl - z.intlBefore, z.intlBefore);
            const col = C[t];
            const pts = z.ring.map(([lon, lat]) => xy(lon, lat).join(",")).join(" ");
            return <polygon key={z.name} points={pts} fill={col} fillOpacity="0.28" stroke={col} strokeWidth="1.5" strokeLinejoin="round" />;
          })}
          {[...zones]
            .sort((a, b) =>
              mode === "present"
                ? Math.abs(b.now - b.before) - Math.abs(a.now - a.before)
                : Math.abs(b.intl - b.intlBefore) - Math.abs(a.intl - a.intlBefore)
            )
            .slice(0, LABELLED)
            .map((z) => {
            const [cx, cy] = centre(z.ring);
            const value = mode === "present" ? z.now : z.intl;
            const delta = mode === "present" ? z.now - z.before : z.intl - z.intlBefore;
            const t = traffic(delta, mode === "present" ? z.before : z.intlBefore);
            const a = fmt(value);
            const b = signed(delta);
            const w = 16 + (a.length + b.length) * 7;
            return (
              <g key={z.name} transform={`translate(${cx - w / 2},${cy - 13})`}>
                <rect width={w} height="26" rx="8" fill="#fff" fillOpacity="0.95" stroke="#E5E6E6" />
                <text x="8" y="17.5" className="epm-pill">
                  <tspan className="epm-pill-v">{a}</tspan>
                  <tspan dx="6" fill={t === "steady" ? "#6B7280" : C[t]}>{b}</tspan>
                </text>
              </g>
            );
          })}
        </svg>

        <div className="epm-card epm-totals">
          <ul>
            {totals.map((r) => (
              <li key={r.label} className={r.group ? "" : "epm-head"}>
                <span className={r.group ? "epm-label-sm" : "epm-label"}>{r.label}</span>
                <span className="epm-figures">
                  {mode === "present" ? (
                    <>
                      <b className={r.group ? "epm-num-sm" : "epm-num"}>{fmt(r.present)}</b>
                      <small>{fmt(r.intl)} intl</small>
                    </>
                  ) : (
                    <b className={r.group ? "epm-num-sm" : "epm-num"}>{fmt(r.intl)}</b>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </div>

        <div className="epm-card epm-stamp">
          Updated <b>13:20</b>
        </div>

        <div className="epm-card epm-switch">
          <div className="epm-seg" role="group" aria-label="What the map shows">
            {[
              ["present", "Present"],
              ["international", "International"],
            ].map(([m, label]) => (
              <button key={m} type="button" aria-pressed={mode === m} onClick={() => setMode(m)}>
                {label}
              </button>
            ))}
          </div>
          <span className="epm-legend">
            <i style={{ background: C.up }} />rising <i style={{ background: C.steady }} />steady <i style={{ background: C.down }} />falling
            <em>· {mode === "present" ? "vs 1h ago" : "vs 10 min ago"}</em>
          </span>
        </div>
      </div>
      <figcaption>
        Illustration with made-up figures; the zones are central Bergen's grunnkretser. Try the Present / International switch.
        Zones © Statistics Norway / Kartverket, CC BY 4.0. Basemap ©{" "}
        <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">OpenStreetMap contributors</a>.
      </figcaption>
    </figure>
  );
};

// --- a day's timeline in the app's chart style ---------------------------------
const day = Array.from({ length: 24 }, (_, h) => {
  const base = 9000 + 7000 * Math.exp(-((h - 13) ** 2) / 18) + (h < 6 ? -1500 : 0);
  const event = h >= 11 && h <= 17 ? 6500 * Math.exp(-((h - 14) ** 2) / 4) : 0;
  return { h, today: Math.round(base + event), week: Math.round(base * 0.97), intl: Math.round((base + event) * (0.08 + (h >= 11 && h <= 18 ? 0.04 : 0))) };
});

export const TimelineMock = () => {
  const w = 640, hgt = 240, L = 48, R = 46, T = 14, B = 30;
  const pw = w - L - R, ph = hgt - T - B;
  const max = 26000, rmax = 4000;
  const x = (i) => L + (i * pw) / 23;
  const y = (v, right) => T + ph - (v / (right ? rmax : max)) * ph;
  const path = (k, right) => day.map((d, i) => `${i ? "L" : "M"}${x(i).toFixed(1)},${y(d[k], right).toFixed(1)}`).join("");
  return (
    <figure className="epm-frame epm-chart">
      <div className="epm-chart-head">
        <span className="epm-label">Event area · today</span>
        <span className="epm-chips">
          <i className="epm-chip-on">Timeline</i>
          <i>By country</i>
          <i>Roaming pulse</i>
        </span>
      </div>
      <svg viewBox={`0 0 ${w} ${hgt}`} role="img" aria-label="Line chart: people present through the day, today against the same day last week, with international presence">
        {[0, 1, 2, 3, 4].map((k) => (
          <g key={k}>
            <line x1={L} x2={w - R} y1={y((max * k) / 4)} y2={y((max * k) / 4)} stroke="rgba(0,0,0,0.06)" />
            <text x={L - 6} y={y((max * k) / 4) + 3} textAnchor="end" className="epm-axis">{fmt((max * k) / 4)}</text>
            <text x={w - R + 6} y={y((rmax * k) / 4, true) + 3} className="epm-axis">{fmt((rmax * k) / 4)}</text>
          </g>
        ))}
        {[0, 6, 12, 18, 23].map((i) => (
          <text key={i} x={x(i)} y={hgt - 10} textAnchor="middle" className="epm-axis">{`${String(i).padStart(2, "0")}:00`}</text>
        ))}
        <path d={path("week")} fill="none" stroke={C.flat} strokeWidth="1.6" strokeDasharray="5 4" />
        <path d={path("intl", true)} fill="none" stroke={C.intl} strokeWidth="1.8" />
        <path d={path("today")} fill="none" stroke={C.brand} strokeWidth="2.6" />
      </svg>
      <div className="epm-legend epm-chart-legend">
        <i style={{ background: C.brand }} />Today <i className="epm-dash" />Same day last week <i style={{ background: C.intl }} />International (right axis)
      </div>
    </figure>
  );
};

// --- the roaming pulse board ----------------------------------------------------
const pulse = zones
  .map((z) => ({ name: z.name, intl: z.intl, d10: z.intl - z.intlBefore, d30: Math.round((z.intl - z.intlBefore) * 1.9) }))
  .sort((a, b) => Math.abs(b.d10) - Math.abs(a.d10))
  .slice(0, 7)
  .sort((a, b) => b.d10 - a.d10);

export const PulseMock = () => (
  <figure className="epm-frame epm-pulse">
    <div className="epm-chart-head">
      <span className="epm-label">Roaming pulse · international presence</span>
      <span className="epm-live"><i />Live</span>
    </div>
    <table>
      <thead>
        <tr>
          <th>Zone</th>
          <th>Now</th>
          <th>10 min</th>
          <th>30 min</th>
        </tr>
      </thead>
      <tbody>
        {pulse.map((p) => (
          <tr key={p.name}>
            <td>{p.name}</td>
            <td><b>{fmt(p.intl)}</b></td>
            {[p.d10, p.d30].map((d, i) => (
              <td key={i} style={{ color: d > 0 ? C.up : d < 0 ? C.down : "#6B7280" }}>{signed(d)}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </figure>
);
